import { ApiError, api } from "../api";
import { type ActiveSession, saveSession } from "../session";
import { requireElement } from "./Hud";

type Mode = "login" | "register" | "forgot" | "reset";

/**
 * Tela de entrada. Resolve com a sessao escolhida: conta autenticada ou
 * visita anonima (fantasma).
 */
export function showAuthOverlay(prefill?: { message?: string; level?: "ok" | "error" }): Promise<ActiveSession> {
  return new Promise((resolve) => {
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = template();
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(selector: string) => overlay.querySelector(selector) as T;

    const notice = $<HTMLParagraphElement>(".notice");
    const tabs = $<HTMLDivElement>(".tabs");
    const tabLogin = $<HTMLButtonElement>('[data-tab="login"]');
    const tabRegister = $<HTMLButtonElement>('[data-tab="register"]');
    const loginForm = $<HTMLFormElement>("#form-login");
    const registerForm = $<HTMLFormElement>("#form-register");
    const forgotForm = $<HTMLFormElement>("#form-forgot");
    const resetForm = $<HTMLFormElement>("#form-reset");
    const forgotBtn = $<HTMLButtonElement>("#btn-forgot");
    const forgotBackBtn = $<HTMLButtonElement>("#btn-forgot-back");
    const divider = $<HTMLDivElement>(".divider");
    const ghostBtn = $<HTMLButtonElement>("#btn-ghost");
    const resendBtn = $<HTMLButtonElement>("#btn-resend");

    const setNotice = (text: string | null, level: "ok" | "error" | "info" = "info") => {
      if (!text) {
        notice.classList.add("hidden");
        return;
      }
      notice.className = `notice ${level === "info" ? "" : level}`.trim();
      notice.textContent = text;
      notice.classList.remove("hidden");
    };

    const setMode = (mode: Mode) => {
      const isAuthMode = mode === "login" || mode === "register";
      tabs.classList.toggle("hidden", !isAuthMode);
      tabLogin.setAttribute("aria-selected", String(mode === "login"));
      tabRegister.setAttribute("aria-selected", String(mode === "register"));
      loginForm.classList.toggle("hidden", mode !== "login");
      registerForm.classList.toggle("hidden", mode !== "register");
      forgotForm.classList.toggle("hidden", mode !== "forgot");
      resetForm.classList.toggle("hidden", mode !== "reset");
      divider.classList.toggle("hidden", !isAuthMode);
      ghostBtn.classList.toggle("hidden", !isAuthMode);
      if (!isAuthMode) resendBtn.classList.add("hidden");
      clearFieldErrors(overlay);
    };

    tabLogin.addEventListener("click", () => setMode("login"));
    tabRegister.addEventListener("click", () => setMode("register"));
    forgotBtn.addEventListener("click", () => {
      setMode("forgot");
      setNotice(null);
      const login = (loginForm.elements.namedItem("login") as HTMLInputElement).value.trim();
      (forgotForm.elements.namedItem("email") as HTMLInputElement).value = login.includes("@") ? login : "";
    });
    forgotBackBtn.addEventListener("click", () => {
      setMode("login");
      setNotice(null);
    });

    // Mensagem vinda do redirect de ativacao, de uma sessao expirada ou de um
    // link de redefinicao de senha.
    const params = new URLSearchParams(location.search);
    const activation = params.get("activation");
    const resetToken = params.get("reset");

    if (resetToken) {
      setMode("reset");
    } else if (activation === "ok") {
      setNotice("E-mail confirmado! Entre para voltar para a cidade.", "ok");
    } else if (activation === "invalid" || activation === "missing") {
      setNotice("Esse link de confirmação é inválido ou já expirou. Peça outro abaixo.", "error");
      resendBtn.classList.remove("hidden");
    } else if (prefill?.message) {
      setNotice(prefill.message, prefill.level ?? "info");
    }
    if (activation || resetToken) history.replaceState(null, "", location.pathname);

    const finish = (session: ActiveSession) => {
      root.replaceChildren();
      resolve(session);
    };

    // ------------------------------------------------------------- login
    loginForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);

      const submit = loginForm.querySelector("button.primary") as HTMLButtonElement;
      const login = (loginForm.elements.namedItem("login") as HTMLInputElement).value.trim();
      const password = (loginForm.elements.namedItem("password") as HTMLInputElement).value;
      const remember = (loginForm.elements.namedItem("remember") as HTMLInputElement).checked;

      submit.disabled = true;
      try {
        const result = await api.login(login, password, remember);
        const session = { token: result.token, expiresAt: result.expiresAt, user: result.user };
        saveSession(session, remember);
        finish({ kind: "player", session });
      } catch (error) {
        submit.disabled = false;
        setNotice(error instanceof Error ? error.message : "Não foi possível entrar", "error");
      }
    });

    // ---------------------------------------------------------- cadastro
    registerForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);

      const submit = registerForm.querySelector("button.primary") as HTMLButtonElement;
      const username = (registerForm.elements.namedItem("username") as HTMLInputElement).value.trim();
      const email = (registerForm.elements.namedItem("email") as HTMLInputElement).value.trim();
      const password = (registerForm.elements.namedItem("password") as HTMLInputElement).value;
      const passwordConfirm = (registerForm.elements.namedItem("passwordConfirm") as HTMLInputElement).value;
      const remember = (registerForm.elements.namedItem("remember") as HTMLInputElement).checked;

      if (password !== passwordConfirm) {
        showFieldErrors(registerForm, { passwordConfirm: "As senhas não coincidem" });
        return;
      }

      submit.disabled = true;
      try {
        // Confirmar o e-mail e opcional: o cadastro ja entra na cidade.
        const result = await api.register(username, email, password, passwordConfirm, remember);
        const session = { token: result.token, expiresAt: result.expiresAt, user: result.user };
        saveSession(session, remember);
        finish({ kind: "player", session });
      } catch (error) {
        if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
          // O formulario como raiz: login e cadastro tem campos com o mesmo nome.
          showFieldErrors(registerForm, error.fields);
          setNotice(null);
        } else {
          setNotice(error instanceof Error ? error.message : "Não foi possível cadastrar", "error");
        }
      } finally {
        submit.disabled = false;
      }
    });

    // ------------------------------------------------------ esqueci a senha
    forgotForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);

      const submit = forgotForm.querySelector("button.primary") as HTMLButtonElement;
      const email = (forgotForm.elements.namedItem("email") as HTMLInputElement).value.trim();

      submit.disabled = true;
      try {
        const result = await api.forgotPassword(email);
        setMode("login");
        setNotice(result.message, "ok");
      } catch (error) {
        setNotice(error instanceof Error ? error.message : "Não foi possível enviar o link", "error");
      } finally {
        submit.disabled = false;
      }
    });

    // -------------------------------------------------------- nova senha
    resetForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearFieldErrors(overlay);

      const submit = resetForm.querySelector("button.primary") as HTMLButtonElement;
      const newPassword = (resetForm.elements.namedItem("newPassword") as HTMLInputElement).value;
      const newPasswordConfirm = (resetForm.elements.namedItem("newPasswordConfirm") as HTMLInputElement).value;

      if (newPassword !== newPasswordConfirm) {
        showFieldErrors(resetForm, { newPasswordConfirm: "As senhas não coincidem" });
        return;
      }

      submit.disabled = true;
      try {
        const result = await api.resetPassword(resetToken ?? "", newPassword, newPasswordConfirm);
        setMode("login");
        setNotice(result.message, "ok");
      } catch (error) {
        submit.disabled = false;
        if (error instanceof ApiError && Object.keys(error.fields).length > 0) {
          showFieldErrors(resetForm, error.fields);
        } else {
          setNotice(error instanceof Error ? error.message : "Não foi possível redefinir a senha", "error");
        }
      }
    });

    // ---------------------------------------------------------- reenvio
    resendBtn.addEventListener("click", async () => {
      const email =
        (registerForm.elements.namedItem("email") as HTMLInputElement).value.trim() ||
        (loginForm.elements.namedItem("login") as HTMLInputElement).value.trim();

      if (!email.includes("@")) {
        setNotice("Digite seu e-mail no campo de cadastro para reenviar o link.", "error");
        setMode("register");
        return;
      }
      resendBtn.disabled = true;
      try {
        const result = await api.resendActivation(email);
        setNotice(result.message, "ok");
      } catch {
        setNotice("Não foi possível reenviar agora. Tente de novo em instantes.", "error");
      } finally {
        resendBtn.disabled = false;
      }
    });

    // ---------------------------------------------------------- fantasma
    ghostBtn.addEventListener("click", () => finish({ kind: "ghost" }));

    if (resetToken) {
      (resetForm.elements.namedItem("newPassword") as HTMLInputElement).focus();
    } else {
      (loginForm.elements.namedItem("login") as HTMLInputElement).focus();
    }
  });
}

export function clearFieldErrors(root: HTMLElement): void {
  root.querySelectorAll(".field .error").forEach((el) => el.remove());
}

export function showFieldErrors(root: HTMLElement, fields: Record<string, string>): void {
  for (const [name, message] of Object.entries(fields)) {
    const input = root.querySelector(`[name="${name}"]`);
    if (!input) continue;
    const error = document.createElement("span");
    error.className = "error";
    error.textContent = message;
    input.parentElement?.append(error);
  }
}

function template(): string {
  return `
    <div class="panel">
      <h1 class="brand">Coité<span>MMO</span></h1>
      <p class="sub">
        Seja bem-vindo a uma versão virtual da cidade de Conceição do Coité.
        Visite a nossa cidade e se divirta com as atrações espalhadas por aí —
        crie uma conta para ter seu avatar ou entre como fantasma só para dar uma olhada.
      </p>

      <p class="notice hidden"></p>

      <div class="tabs" role="tablist">
        <button type="button" role="tab" data-tab="login" aria-selected="true">Entrar</button>
        <button type="button" role="tab" data-tab="register" aria-selected="false">Criar conta</button>
      </div>

      <form id="form-login" novalidate>
        <div class="field">
          <label for="login-login">Usuário ou e-mail</label>
          <input id="login-login" name="login" type="text" autocomplete="username" required />
        </div>
        <div class="field">
          <label for="login-password">Senha</label>
          <input id="login-password" name="password" type="password" autocomplete="current-password" required />
        </div>
        <label class="remember">
          <input name="remember" type="checkbox" checked />
          Lembrar de mim
        </label>
        <button type="submit" class="primary">Entrar na cidade</button>
        <button type="button" id="btn-forgot" class="link-btn" style="margin-top:10px">Esqueceu sua senha?</button>
      </form>

      <form id="form-forgot" class="hidden" novalidate>
        <div class="field">
          <label for="forgot-email">E-mail da conta</label>
          <input id="forgot-email" name="email" type="email" autocomplete="email" required />
        </div>
        <button type="submit" class="primary">Enviar link de redefinição</button>
        <button type="button" id="btn-forgot-back" class="link-btn" style="margin-top:10px">Voltar para o login</button>
      </form>

      <form id="form-reset" class="hidden" novalidate>
        <div class="field">
          <label for="reset-password">Nova senha (8+ caracteres, com letras e números)</label>
          <input id="reset-password" name="newPassword" type="password" autocomplete="new-password"
                 minlength="8" required />
        </div>
        <div class="field">
          <label for="reset-password-confirm">Confirme a nova senha</label>
          <input id="reset-password-confirm" name="newPasswordConfirm" type="password" autocomplete="new-password"
                 minlength="8" required />
        </div>
        <button type="submit" class="primary">Redefinir senha</button>
      </form>

      <form id="form-register" class="hidden" novalidate>
        <div class="field">
          <label for="reg-username">Nome do personagem</label>
          <input id="reg-username" name="username" type="text" autocomplete="username"
                 minlength="3" maxlength="16" required />
        </div>
        <div class="field">
          <label for="reg-email">E-mail</label>
          <input id="reg-email" name="email" type="email" autocomplete="email" required />
        </div>
        <div class="field">
          <label for="reg-password">Senha (8+ caracteres, com letras e números)</label>
          <input id="reg-password" name="password" type="password" autocomplete="new-password"
                 minlength="8" required />
        </div>
        <div class="field">
          <label for="reg-password-confirm">Confirme a senha</label>
          <input id="reg-password-confirm" name="passwordConfirm" type="password" autocomplete="new-password"
                 minlength="8" required />
        </div>
        <label class="remember">
          <input name="remember" type="checkbox" checked />
          Lembrar de mim
        </label>
        <button type="submit" class="primary">Criar conta</button>
      </form>

      <button type="button" id="btn-resend" class="link-btn hidden" style="margin-top:12px">
        reenviar e-mail de confirmação
      </button>

      <div class="divider">ou</div>
      <button type="button" id="btn-ghost" class="secondary">Visitar como fantasma</button>
    </div>
  `;
}
